/**
 * meshLighting — makes the scene's authored lights reach imported 3D models.
 *
 * The lights in `config.lights` are real `pc.LightComponent`s (see
 * createViewer's createPointLight / createDirectionalLightEntity /
 * createSpotLight). Splat relighting reads those same entities every frame and
 * feeds them to the gsplat shader, so the splats always see them. Custom
 * GLB/GLTF meshes, however, render on the dedicated HotspotMeshLayer (for depth
 * sorting against the splats) and a PlayCanvas light only illuminates mesh
 * instances on layers the light belongs to — the default is the World layer
 * only. That is why models looked unlit while the splats took the lights.
 *
 * This module owns the pure config→light mapping that closes the gap, without
 * touching the relighting shader or its uniforms:
 *   - which layers each authored light must belong to (World + mesh layers),
 *   - the light-count cap shared with the relighting shader (16),
 *   - the ambient term meshes receive so they match the relighting ambient,
 *   - the unlit material recipe behind `CustomMeshConfig.unlit`.
 */
import * as pc from 'playcanvas';

/**
 * Maximum number of authored lights that reach custom meshes. Matches
 * MAX_RELIGHT_LIGHTS in effects/gsplat-relighting.ts so a model sees exactly
 * the lights the splats see, and bounds the per-layer clustered-light cost.
 * Lights beyond the cap keep their World-layer membership (unchanged behaviour).
 */
export const MAX_MESH_LIGHTS = 16;

/** Minimal shape of a pc.LightComponent's layer membership. */
export interface LightLayerTarget {
    /** pc.LightComponent exposes a ReadonlyArray getter and a setter that takes a new array. */
    layers: ReadonlyArray<number>;
}

/**
 * Layers the light at `index` (in scene light order) must belong to so it
 * lights both the splats' World layer and the custom-mesh layers.
 */
export function resolveMeshLightLayers(index: number, worldLayerId: number, meshLayerIds: readonly number[]): number[] {
    const layers = [worldLayerId];
    if (index < MAX_MESH_LIGHTS) {
        for (const id of meshLayerIds) {
            if (!layers.includes(id)) layers.push(id);
        }
    }
    return layers;
}

function sameLayerSet(a: readonly number[], b: readonly number[]): boolean {
    if (a.length !== b.length) return false;
    for (const id of a) if (!b.includes(id)) return false;
    return true;
}

/**
 * Apply resolveMeshLightLayers to every light component in `lights` (scene
 * order; null entries are skipped). Only writes `layers` when the membership
 * actually changes, since the setter removes/re-adds the light on every layer.
 * Returns the number of lights whose layers changed.
 */
export function syncMeshLightLayers(
    lights: ReadonlyArray<LightLayerTarget | null | undefined>,
    worldLayerId: number,
    meshLayerIds: readonly number[]
): number {
    let changed = 0;
    let index = 0;
    for (const light of lights) {
        if (!light) continue;
        const target = resolveMeshLightLayers(index, worldLayerId, meshLayerIds);
        index++;
        if (sameLayerSet(light.layers, target)) continue;
        light.layers = target;
        changed++;
    }
    return changed;
}

export interface AmbientRgb {
    r: number;
    g: number;
    b: number;
}

/**
 * Ambient light meshes should receive.
 *
 * Splat relighting multiplies each splat by `ambient + Σ lights`, where ambient
 * is `splatRelighting.ambientColor × ambientIntensity`. A Lambert mesh under
 * `scene.ambientLight` gets `albedo × (ambient + Σ lights)`, so mirroring the
 * relighting ambient onto the scene gives models the same base level as the
 * splats. When relighting is not active the legacy ambient (black by default,
 * or whatever an ambient/hemispheric light or skybox IBL wrote) is kept.
 */
export function resolveMeshAmbient(opts: {
    relightingActive: boolean;
    relightAmbient: AmbientRgb | null | undefined;
    baseAmbient: AmbientRgb;
    /**
     * False while nothing in the scene has set an ambient (no ambient / hemispheric light,
     * no skybox IBL): models then get DEFAULT_MESH_AMBIENT instead of black, so the side of a
     * model facing away from every light reads as shade rather than a black cut-out.
     * Omitted = true (the legacy behaviour).
     */
    baseAmbientSet?: boolean;
}): AmbientRgb {
    const { relightingActive, relightAmbient, baseAmbient, baseAmbientSet = true } = opts;
    if (relightingActive && relightAmbient) {
        return {
            r: Math.max(0, relightAmbient.r),
            g: Math.max(0, relightAmbient.g),
            b: Math.max(0, relightAmbient.b),
        };
    }
    if (!baseAmbientSet) return { ...DEFAULT_MESH_AMBIENT };
    return { r: baseAmbient.r, g: baseAmbient.g, b: baseAmbient.b };
}

/**
 * Ambient for models in a scene that sets none (relighting off, no skybox, no ambient light).
 * The same level the skybox IBL approximation gives (0.3, 0.3, 0.35 × intensity), slightly
 * neutral, so models look alike with or without a skybox.
 */
export const DEFAULT_MESH_AMBIENT: Readonly<AmbientRgb> = { r: 0.3, g: 0.3, b: 0.32 };

export function ambientEquals(a: AmbientRgb, b: AmbientRgb): boolean {
    return a.r === b.r && a.g === b.g && a.b === b.b;
}

// ---------------------------------------------------------------------------
// Unlit models (CustomMeshConfig.unlit)
// ---------------------------------------------------------------------------

interface LitSnapshot {
    useLighting: boolean;
    useSkybox: boolean;
    diffuse: pc.Color;
    diffuseMap: pc.Texture | null;
    diffuseVertexColor: boolean;
    emissive: pc.Color;
    emissiveMap: pc.Texture | null;
    emissiveMapUv: number;
    emissiveMapTiling: pc.Vec2;
    emissiveMapOffset: pc.Vec2;
    emissiveMapRotation: number;
    emissiveMapChannel: string;
    emissiveVertexColor: boolean;
    emissiveVertexColorChannel: string;
    emissiveIntensity: number;
}

/** Original lit state of materials this module switched to unlit, keyed by material. */
const litSnapshots = new WeakMap<pc.StandardMaterial, LitSnapshot>();

/**
 * Switch a StandardMaterial to the same unlit representation the engine's glTF
 * loader uses for KHR_materials_unlit: the base colour (and map) moves to the
 * emissive slot so the shader outputs flat albedo, and lighting/skybox are
 * turned off. Materials that were already unlit when authored are left alone.
 * Returns true when the material was changed.
 */
export function applyUnlitToMaterial(mat: pc.StandardMaterial): boolean {
    if (litSnapshots.has(mat)) return false;
    if (!mat.useLighting) return false; // authored unlit (KHR_materials_unlit) — nothing to do
    litSnapshots.set(mat, {
        useLighting: mat.useLighting,
        useSkybox: mat.useSkybox,
        diffuse: mat.diffuse.clone(),
        diffuseMap: mat.diffuseMap,
        diffuseVertexColor: mat.diffuseVertexColor,
        emissive: mat.emissive.clone(),
        emissiveMap: mat.emissiveMap,
        emissiveMapUv: mat.emissiveMapUv,
        emissiveMapTiling: mat.emissiveMapTiling.clone(),
        emissiveMapOffset: mat.emissiveMapOffset.clone(),
        emissiveMapRotation: mat.emissiveMapRotation,
        emissiveMapChannel: mat.emissiveMapChannel,
        emissiveVertexColor: mat.emissiveVertexColor,
        emissiveVertexColorChannel: mat.emissiveVertexColorChannel,
        emissiveIntensity: mat.emissiveIntensity,
    });
    mat.emissive.copy(mat.diffuse);
    mat.emissiveMap = mat.diffuseMap;
    mat.emissiveMapUv = mat.diffuseMapUv;
    mat.emissiveMapTiling.copy(mat.diffuseMapTiling);
    mat.emissiveMapOffset.copy(mat.diffuseMapOffset);
    mat.emissiveMapRotation = mat.diffuseMapRotation;
    mat.emissiveMapChannel = mat.diffuseMapChannel;
    mat.emissiveVertexColor = mat.diffuseVertexColor;
    mat.emissiveVertexColorChannel = mat.diffuseVertexColorChannel;
    mat.emissiveIntensity = 1;
    mat.useLighting = false;
    mat.useSkybox = false;
    mat.diffuse.set(1, 1, 1);
    mat.diffuseMap = null;
    mat.diffuseVertexColor = false;
    mat.update();
    return true;
}

/**
 * Undo applyUnlitToMaterial. Returns true when the material was restored;
 * false when this module never switched it (e.g. authored-unlit materials).
 */
export function restoreLitMaterial(mat: pc.StandardMaterial): boolean {
    const s = litSnapshots.get(mat);
    if (!s) return false;
    litSnapshots.delete(mat);
    mat.useLighting = s.useLighting;
    mat.useSkybox = s.useSkybox;
    mat.diffuse.copy(s.diffuse);
    mat.diffuseMap = s.diffuseMap;
    mat.diffuseVertexColor = s.diffuseVertexColor;
    mat.emissive.copy(s.emissive);
    mat.emissiveMap = s.emissiveMap;
    mat.emissiveMapUv = s.emissiveMapUv;
    mat.emissiveMapTiling.copy(s.emissiveMapTiling);
    mat.emissiveMapOffset.copy(s.emissiveMapOffset);
    mat.emissiveMapRotation = s.emissiveMapRotation;
    mat.emissiveMapChannel = s.emissiveMapChannel;
    mat.emissiveVertexColor = s.emissiveVertexColor;
    mat.emissiveVertexColorChannel = s.emissiveVertexColorChannel;
    mat.emissiveIntensity = s.emissiveIntensity;
    mat.update();
    return true;
}

/** Whether this module currently holds the material in its unlit form. */
export function isUnlitByViewer(mat: pc.StandardMaterial): boolean {
    return litSnapshots.has(mat);
}

/** Minimal node shape walked by setEntityUnlit (matches pc.Entity). */
export interface RenderNodeLike {
    render?: { meshInstances?: ReadonlyArray<{ material: pc.Material | null }> } | null;
    children?: ReadonlyArray<RenderNodeLike>;
}

/**
 * Apply or remove the unlit recipe on every StandardMaterial under `root`.
 * Returns the number of materials changed.
 */
export function setEntityUnlit(root: RenderNodeLike, unlit: boolean): number {
    let changed = 0;
    const seen = new Set<pc.Material>();
    const visit = (node: RenderNodeLike): void => {
        const instances = node.render?.meshInstances;
        if (instances) {
            for (const mi of instances) {
                const mat = mi.material;
                if (!mat || seen.has(mat)) continue;
                seen.add(mat);
                if (!(mat instanceof pc.StandardMaterial)) continue;
                if (unlit ? applyUnlitToMaterial(mat) : restoreLitMaterial(mat)) changed++;
            }
        }
        if (node.children) {
            for (const child of node.children) visit(child);
        }
    };
    visit(root);
    return changed;
}
